import React from 'react';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import ColumnLayout from '@cloudscape-design/components/column-layout';

interface StreamKey {
  fields?: string[];
  delimiter?: string;
}

interface StreamKeyInputProps {
  streamKey?: StreamKey | null;
  stream?: string;
  onStreamChange?: (value: string) => void;
  keyFields?: Record<string, string>;
  onKeyFieldsChange?: (fields: Record<string, string>) => void;
  disabled?: boolean;
  onKeyDown?: (event: CustomEvent) => void;
  streamOptions?: { value: string }[];
}

export function StreamKeyInput({ streamKey, stream, onStreamChange, keyFields, onKeyFieldsChange, disabled, onKeyDown, streamOptions }: StreamKeyInputProps) {
  if (streamKey?.fields && streamKey.fields.length > 1) {
    return (
      <ColumnLayout columns={Math.min(streamKey.fields.length, 3)}>
        {streamKey.fields.map(fieldName => (
          <FormField key={fieldName} label={fieldName}>
            <Input
              value={keyFields?.[fieldName] || ''}
              onChange={({ detail }) => {
                onKeyFieldsChange?.({ ...keyFields, [fieldName]: detail.value });
              }}
              onKeyDown={onKeyDown}
              placeholder={`Enter ${fieldName}`}
              disabled={disabled}
            />
          </FormField>
        ))}
      </ColumnLayout>
    );
  }

  return (
    <FormField label="Stream">
      <Autosuggest
        value={stream || ''}
        onChange={({ detail }) => onStreamChange?.(detail.value)}
        onKeyDown={onKeyDown}
        placeholder="e.g., order-456"
        disabled={disabled}
        options={streamOptions || []}
        enteredTextLabel={(value) => `Use: "${value}"`}
        empty="No streams found"
      />
    </FormField>
  );
}

export function buildKeyPayload(streamKey?: StreamKey | null, stream?: string, keyFields?: Record<string, string>): { stream?: string; keyFields?: Record<string, string> } {
  if (streamKey?.fields && streamKey.fields.length > 1 && keyFields) {
    return { keyFields };
  }
  return { stream: stream || '' };
}

export function isKeyComplete(streamKey?: StreamKey | null, stream?: string, keyFields?: Record<string, string>): boolean {
  if (streamKey?.fields && streamKey.fields.length > 1) {
    return streamKey.fields.every(f => keyFields?.[f]?.trim());
  }
  return !!stream?.trim();
}

export function buildStreamDisplay(streamKey?: StreamKey | null, keyFields?: Record<string, string>): string {
  if (streamKey?.fields && keyFields) {
    const delimiter = streamKey.delimiter || '#';
    return streamKey.fields.map(f => keyFields[f] || '').join(delimiter);
  }
  return '';
}
